import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import { LockIcon, CheckIcon, TrophyIcon, ScaleIcon, ClipboardIcon, PeopleIcon } from '../shared/Icons';
import CountryDisplay from '../shared/CountryDisplay';
import { useListFrom } from '../../hooks/useUrlParam';
import type { Tournament, Bout } from '../../types';
import AgeGroupPills from './AgeGroupPills';
import { TournamentTypeBadge, TournamentInfoPills } from './TournamentPills';
import TournamentDates from './TournamentDates';
import TournamentBanner from './TournamentBanner';
import LiveBoutRows from './LiveBoutRows';

interface TournamentCardProps {
  tournament: Tournament;
  liveBouts?: Bout[];
  /** Show pool/TM badges (used on Tournaments page) */
  showBadges?: boolean;
  /** Highlight date in green (today's events) */
  isToday?: boolean;
  /** Hide banner image (for recent/past events) */
  compact?: boolean;
}

export default function TournamentCard({ tournament: tr, liveBouts = [], showBadges = false, isToday = false, compact = false }: TournamentCardProps) {
  const { t } = useI18n();
  const { boxerCount } = useLabels();
  // #595 — the detail page goes back to this list with its filters and its position.
  const listFrom = useListFrom();
  // #615 — the pills under the text. With none of them, the card draws no
  // line under the text.
  const showStateRow = (tr.registration_count ?? 0) > 0 || !!(tr.is_locked && tr.is_tournament_manager);
  const cardDeadline = showBadges ? tr.registration_deadline : null;
  const hasInfoPills = !!(cardDeadline || tr.facebook_url || tr.livestream_url);
  const showRoles = showBadges && !!(tr.is_registered || tr.is_tournament_manager || tr.is_in_referee_pool);

  return (
    <div className="card overflow-hidden hover:shadow-md transition-shadow">
      {/* #615 — every logo is a banner, as at the top of the tournament
          page. A compact card (past events) shows no logo. */}
      {tr.logo_url && !compact && (
        // The link of the name below carries the name: this link is for the
        // pointer only, so a screen reader and the Tab key skip it.
        <Link to={`/tournaments/${tr.id}`} state={listFrom} aria-hidden="true" tabIndex={-1}>
          <TournamentBanner url={tr.logo_url} maxHeight="max-h-40" />
        </Link>
      )}

      {/* Main card row */}
      <Link to={`/tournaments/${tr.id}`} state={listFrom}
        className="p-4 sm:p-5 flex gap-3 sm:gap-4 hover:bg-gray-50 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-base">{tr.name}</h2>
              {/* #615 — the type under the name, as in the header of the
                  tournament page. */}
              <div className="flex mt-1"><TournamentTypeBadge type={tr.type} where="card" /></div>
              <AgeGroupPills groups={tr.allowed_age_groups} testId="tournament-card-age-groups" className="mt-1" />
            </div>
            <TournamentDates start={tr.date_start} end={tr.date_end} isToday={isToday} testId="tournament-card-dates" />
          </div>
          {/* #615 — a line between the pills of the tournament and the
              text, as in the header of the tournament page. */}
          <div className="border-t border-gray-100 my-2" />
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <span className="min-w-0">{tr.location}</span>
            {tr.country && <CountryDisplay code={tr.country} size="sm" />}
          </div>
          {/* #532 — the description, under the location (operator,
              2026-09-30; plain text: React escapes it; the first 3 lines
              only). */}
          {tr.description && (
            <p data-testid="tournament-card-description" className="text-xs text-gray-500 mt-1 whitespace-pre-line line-clamp-3">{tr.description}</p>
          )}
          {/* #615 — a line between the text and the state, link and role
              pills, only when a pill shows. The main pills are the `badge`,
              12 px and 20 px tall. The roles are the `pill`, also 12 px and
              20 px tall (index.css). */}
          {(showStateRow || hasInfoPills || showRoles) && (
          <>
          <div className="border-t border-gray-100 my-2" />
          <div className="flex flex-col gap-1.5">
          {showStateRow && (
          <div className="flex flex-wrap items-center gap-1.5">
            {/* #551 — the registered boxers (operator, 2026-10-03); none: no badge.
                #570 — the colours of the registration panel (RegistrationPanel.tsx):
                its background, its border and the text of its count line. */}
            {(tr.registration_count ?? 0) > 0 && (
              <span data-testid="tournament-card-boxers" className="badge gap-1 bg-green-50 text-green-700 ring-1 ring-inset ring-green-200">
                <PeopleIcon className="w-3.5 h-3.5" /> {boxerCount(tr.registration_count ?? 0)}
              </span>
            )}
            {tr.is_locked && tr.is_tournament_manager && (
              <span className="badge badge-gray" title={t.tournament.boutsLocked}><LockIcon className="w-3.5 h-3.5" /></span>
            )}
          </div>
          )}
          <TournamentInfoPills where="card"
            deadline={cardDeadline}
            facebookUrl={tr.facebook_url} livestreamUrl={tr.livestream_url} />

          {/* Assignment badges */}
          {showRoles && (
            <div className="flex flex-wrap gap-1.5">
              {tr.is_registered && (
                <span data-testid="tournament-card-role" className="pill text-green-700 bg-green-50 ring-green-200">
                  <CheckIcon className="w-3 h-3" /> {t.tournament.inBoxerPool}
                </span>
              )}
              {tr.is_tournament_manager && (
                <span data-testid="tournament-card-role" className="pill text-orange-700 bg-orange-50 ring-orange-300">
                  <TrophyIcon className="w-3 h-3" /> {t.tournament.assignedTM}
                </span>
              )}
              {tr.is_in_referee_pool && !tr.is_in_jury_pool && (
                <span data-testid="tournament-card-role" className="pill text-blue-700 bg-blue-50 ring-blue-200">
                  <ScaleIcon className="w-3 h-3" /> {t.tournament.inRefPool}
                </span>
              )}
              {tr.is_in_referee_pool && tr.is_in_jury_pool && (
                <span data-testid="tournament-card-role" className="pill text-purple-700 bg-purple-100 ring-purple-300">
                  <ClipboardIcon className="w-3 h-3" /> {t.tournament.inJuryPool}
                </span>
              )}
            </div>
          )}
          </div>
          </>
          )}
        </div>
      </Link>

      {/* Live bout rows */}
      {liveBouts.length > 0 && <LiveBoutRows bouts={liveBouts} variant="card" />}
    </div>
  );
}
